<template>
  <div class="chart-wrap">
    <apexchart
      width="100%"
      :options="options"
      :series="series"
      height="100%"
    />
  </div>
</template>

<script>
import ApexCharts from 'vue3-apexcharts'

export default {
  name: 'SessionsLineChart',
  components: { apexchart: ApexCharts },
  props: {
    rawData: { type: Object, required: true },
    // currentRaw должен быть одним из ключей: 'today','last_7_days','current_week','current_month','last_90_days','current_year','all_time'
    currentRaw: { type: String, default: 'today' }
  },
  computed: {
    // Берём список сессий
    list () {
      return (this.rawData && this.rawData.list) || []
    },

    // Определяем период и шаг агрегации в зависимости от currentRaw
    rangeSpec () {
      const tz = 'Europe/Warsaw'
      const now = new Date()

      const startOfDay = (d) => {
        const r = new Date(d)
        r.setHours(0,0,0,0)
        return r
      }

      const toKeyDay = (d) => {
        // локальная дата ключ YYYY-MM-DD
        return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      }

      const toKeyHour = (d) => `${toKeyDay(d)}T${String(d.getHours()).padStart(2,'0')}`
      const toKeyMonth = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`

      const spec = { unit: 'day', start: startOfDay(now), end: now }

      switch (this.currentRaw) {
        case 'today':
          // 24 часов: агрегируем по часам
          const startToday = startOfDay(now)
          return { unit: 'hour', start: startToday, end: now }
        case 'last_7_days':
          {
            const start = new Date(now)
            start.setDate(start.getDate() - 6) // включая сегодня -> 7 дней
            start.setHours(0,0,0,0)
            return { unit: 'day', start, end: now }
          }
        case 'current_week':
          {
            // неделя с понедельника
            const d = new Date(now)
            const day = d.getDay() || 7 // Sunday -> 7
            const diff = day - 1
            const start = new Date(d)
            start.setDate(d.getDate() - diff)
            start.setHours(0,0,0,0)
            return { unit: 'day', start, end: now }
          }
        case 'current_month':
          {
            const start = new Date(now.getFullYear(), now.getMonth(), 1)
            start.setHours(0,0,0,0)
            return { unit: 'day', start, end: now }
          }
        case 'last_90_days':
          {
            const start = new Date(now)
            start.setDate(start.getDate() - 89) // включая сегодня
            start.setHours(0,0,0,0)
            return { unit: 'day', start, end: now }
          }
        case 'current_year':
          {
            const start = new Date(now.getFullYear(), 0, 1)
            start.setHours(0,0,0,0)
            return { unit: 'month', start, end: now }
          }
        case 'all_time':
          {
            // берем диапазон от самой ранней сессии до сейчас, агрегируем по месяцу если большой диапазон, иначе по дням
            if (!this.list.length) return { unit: 'day', start: startOfDay(now), end: now }
            const dates = this.list
              .map(i => i.entry_time)
              .filter(Boolean)
              .map(s => new Date(s))
              .filter(d => !isNaN(d))
            const min = new Date(Math.min(...dates.map(d => d.getTime())))
            min.setHours(0,0,0,0)
            const diffDays = Math.ceil((now - min) / (1000*60*60*24))
            return { unit: diffDays > 365 ? 'month' : 'day', start: min, end: now }
          }
        default:
          return spec
      }
    },

    // Генерация списка ключей (дат) и человекочитаемых меток (на русском) для оси X
    timeline () {
      const tz = 'Europe/Warsaw'
      const fmtDay = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'short', timeZone: 'Europe/Warsaw' })
      const fmtHour = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', day: '2-digit', month: 'short', timeZone: 'Europe/Warsaw' })
      const fmtMonth = new Intl.DateTimeFormat('ru-RU', { month: 'short', year: 'numeric', timeZone: 'Europe/Warsaw' })

      const unit = this.rangeSpec.unit
      const start = new Date(this.rangeSpec.start)
      const end = new Date(this.rangeSpec.end)

      const keys = []
      const labels = []

      const push = (date) => {
        if (unit === 'hour') {
          keys.push(`${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}T${String(date.getHours()).padStart(2,'0')}`)
          labels.push(fmtHour.format(date))
        } else if (unit === 'month') {
          keys.push(`${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`)
          labels.push(fmtMonth.format(date))
        } else {
          keys.push(`${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`)
          labels.push(fmtDay.format(date))
        }
      }

      const cur = new Date(start)
      if (unit === 'hour') {
        cur.setMinutes(0,0,0)
        while (cur <= end) {
          push(new Date(cur))
          cur.setHours(cur.getHours() + 1)
        }
      } else if (unit === 'month') {
        cur.setDate(1); cur.setHours(0,0,0,0)
        while (cur <= end) {
          push(new Date(cur))
          cur.setMonth(cur.getMonth() + 1)
        }
      } else {
        cur.setHours(0,0,0,0)
        while (cur <= end) {
          push(new Date(cur))
          cur.setDate(cur.getDate() + 1)
        }
      }

      return { keys, labels }
    },

    // Считаем количество сессий по сгенерированной шкале (ключи)
    aggregated () {
      const unit = this.rangeSpec.unit
      const keys = this.timeline.keys

      // Инициализируем map с нулями чтобы линия была непрерывной
      const map = new Map(keys.map(k => [k, 0]))

      for (const item of this.list) {
        if (!item || !item.entry_time) continue
        const d = new Date(item.entry_time)
        if (isNaN(d)) continue

        let key
        if (unit === 'hour') key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}`
        else if (unit === 'month') key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
        else key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`

        if (map.has(key)) map.set(key, map.get(key) + 1)
      }

      const arr = Array.from(map.entries()).map(([k, v]) => ({ key: k, count: v }))
      return arr
    },

    // Серия для ApexCharts: количество сессий по временной шкале
    series () {
      const agg = this.aggregated
      return [ { name: 'Количество сессий', type: 'line', data: agg.map(i => i.count) } ]
    },

    options () {
      const labels = this.timeline.labels
      return {
        chart: {
          id: 'sessions-chart',
          type: 'line',
          zoom: { enabled: false },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: true
            }
          },
          background: '#272e39',
          foreColor: '#E6EEF8'
        },
        theme: { mode: 'dark' },
        stroke: { width: 3, curve: 'smooth' },
        markers: { size: 4, hover: { sizeOffset: 3 } },
        xaxis: {
          type: 'category',
          categories: labels,
          title: { text: 'Дата' },
          labels: { rotate: -45, style: { colors: '#E6EEF8' } }
        },
        yaxis: {
          title: { text: 'Количество сессий' },
          labels: { formatter: val => Math.round(val) }
        },
        tooltip: {
          shared: false,
          y: {
            formatter: (val) => `${val} ${this.pluralizeSessions(val)}`
          }
        },
        legend: { show: false },
        noData: { text: 'Нет данных для отображения' },
        responsive: [ { breakpoint: 5, options: { chart: { height: 150 } } } ]
      }
    }
  },
  methods: {
    pluralizeSessions (n) {
      const val = Number(n) || 0
      const mod10 = val % 10
      const mod100 = val % 100
      if (mod100 >= 11 && mod100 <= 14) return 'сессий'
      if (mod10 === 1) return 'сессия'
      if (mod10 >=2 && mod10 <=4) return 'сессии'
      return 'сессий'
    }
  }
}
</script>



<style scoped>
.vue-apexcharts {
      height: 100%;
}
.chart-wrap {
  width: 100%;
  max-height: 16rem;
  overflow: hidden;
  background: #272e39;
  padding: 12px;
  border-radius: 12px;
  color: #E6EEF8;
  height: 100%;
  min-height: 25rem;
}



/* иногда полезно дополнительно ограничить SVG внутри */
.chart-wrap .apexcharts-canvas svg {
  max-height: 100%;
  height: 100%;
}

</style>
